import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Dialog,
  Divider,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Slide,
  Typography,
} from "@mui/material";
import {
  GTAScreenType,
  HlLobCode,
  privacyPolicyTitle,
  SCREEN_TYPE,
  termsAndConditionsTitle,
} from "../../../utils/constants";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ABCLogo from "../../../assets/images/ABC-Service.jpg";
import { TransitionProps } from "@mui/material/transitions";
import "./HamburgerMenuDialog.scss";
import MyProfile from "../MyProfile/MyProfile";
import Utilities from "../Utilities/Utilities";
import HelpAndSupport from "../HelpAndSupport/HelpAndSupport";
import AppSettings from "../AppSettings/AppSettings";
import FAQs from "../FAQs/FAQs";
import DeviceAuthenticationSettings from "../DeviceAuthenticationSettings/DeviceAuthenticationSettings";
import ChangemPIN from "../ChangemPIN/ChangemPIN";
import PolicyAndConditionsComponent from "../../../components/CustomPolicyAndConditionsComponent/PolicyAndConditionsComponent";
import LogOutPopUp from "../LogOutPopUp/LogOutPopUP";
import { useAppSelector } from "../../../redux-store/CustomReduxHooks";
import { getInitials } from "../../../utils/helper";
import CustomNetworkImage from "../../../components/CustomNetworkImage/CustomNetworkImage";
import RateUsPopUp from "../RateUsPopUp/RateUsPopUp";
import AppPermissions from "../AppPermissions/AppPermissions";
import { isEmpty, sortBy } from "lodash";
import { triggerCleverTapEvent } from "../../../utils/CleverTap";
import { CleverTapEventType } from "../../../utils/CleverTapConstant";
import { CT_ScreenEvntDtls } from "../../Feature_CleverTap/ScreenEvents/CleverTapEventDetails";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import TriggerGTAEvent from "../../../utils/GTA";
import { T_Menu_List } from "../GeneralAppElements_Types";
import { R_HomeRoutes } from "../../../utils/Routes";
import { E_ChannelOptions } from "../../../globalTypes/GlobalTypes";
import { useAppTranslation } from "../../../hooks/useAppTranslation";
import LanguageSelectionPopup from "../../../pages/Home/LanguageSelectionPopup/LanguageSelectionPopup";

interface Props {
  open: boolean;
  setOpen: Function;
  className?: string;
  fromHomeSection?: boolean;
  defaultVisibleComponent?: string;
}

export const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const HamburgerMenuDialog: FC<Props> = ({
  open,
  setOpen,
  className = "",
  fromHomeSection = false,
  defaultVisibleComponent = "main-menu",
}) => {
  const [visibleComponent, setVisibleComponent] = useState<string>(
    defaultVisibleComponent,
  );
  const [isCustomPopupOpen, setIsCustomPopupOpen] = useState<boolean>(false);
  const [logoutPopupOpen, setLogoutPopupOpen] = useState<boolean>(false);
  const [openRateUsPopUp, setOpenRateUsPopUp] = useState<boolean>(false);
  const [languagePopupOpen, setLanguagePopupOpen] = useState<boolean>(false);
  const [finalPolicyAndTermsTitle, setFinalPolicyAndTermsTitle] =
    useState<string>("");
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const isSuperApp = csAppSlice.channel === E_ChannelOptions.SUPER_APP;
  const userDetails = csAppSlice.userDetails;

  const navigate = useNavigate();
  const location = useLocation();
  const [URLSearchParam] = useSearchParams();
  const action = URLSearchParam.get("action") || "";
  const module = URLSearchParam.get("module") || "";
  const { t } = useAppTranslation("common");

  useEffect(() => {
    if (visibleComponent === "main-menu") {
      triggerCleverTapEvent(CleverTapEventType.SCREEN_EVENT, {
        ScreenName: CT_ScreenEvntDtls.R_GAE.gaHambMenuDetail.screenName,
        ScreenType: SCREEN_TYPE.DashboardScreenType,
      });
    }
    if (action === "FAQs") {
      setVisibleComponent("FAQs");
    } else if (action === "myProfile") {
      setVisibleComponent("my-profile");
    } else if (action === "utilities") {
      setVisibleComponent("utilities");
    } else if (action === "helpAndSupport") {
      setVisibleComponent("help-&-support");
    } else if (action === "appSettings") {
      isEmpty(csAppSlice.NativeDeviceInfo?.deviceName)
        ? setVisibleComponent("main-menu")
        : setVisibleComponent("app-settings");
    } else if (action === "logout") {
      setLogoutPopupOpen(true);
    } else if (module === "rateUs") {
      setOpenRateUsPopUp(true);
    } else if (module === "termsAndCondition") {
      handlePolicyTermsPopup(termsAndConditionsTitle);
    }
  }, []);

  const handleCleverTapCTAClick = (item: string) => {
    triggerCleverTapEvent("serv_hamburger_menu_option_click", {
      ScreenName: "Servicing Hamburger Menu Details Screen",
      ScreenType: SCREEN_TYPE.generalAppType,
      isPrevPageNameRequired: true,
      ctaText: item,
    });
  };

  const handleGTMCTAClick = (item: string) => {
    TriggerGTAEvent("serv_hamburger_menu_option_click", {
      screen_type: GTAScreenType.DashboardScreenType,
      screen_name: "servicing_hamburger_menu_details_screen",
      CTA_text: item,
    });
  };

  const handleCleverTapBottomCTAClick = (item: string) => {
    triggerCleverTapEvent("serv_hamburger_bottom_cta_click", {
      ScreenName: "Servicing Hamburger Menu Details Screen",
      ScreenType: SCREEN_TYPE.generalAppType,
      isPrevPageNameRequired: true,
      ctaText: item,
    });
  };

  const handleGTMBottomCTAClick = (item: string) => {
    TriggerGTAEvent("serv_hamburger_bottom_cta_click", {
      screen_type: GTAScreenType.DashboardScreenType,
      screen_name: "servicing_hamburger_menu_details_screen",
      CTA_text: item,
    });
  };

  const handleMenuClick = (item: T_Menu_List) => {
    handleCleverTapCTAClick(item.menuDisplayName);
    handleGTMCTAClick(item.menuDisplayName);
    TriggerGTAEvent("serv_profile_menu_cta_click", {
      screen_type: GTAScreenType.DashboardScreenType,
      screen_name: "servicing_hamburger_menu_details_screen",
      CTA_text: item.menuDisplayName,
    });

    if (item.menuName === "Log out") {
      setLogoutPopupOpen(true);
    } else if (item.menuName === "Change Language") {
      setLanguagePopupOpen(true);
    } else {
      item.pathName && setVisibleComponent(item.pathName);
    }
  };

  const handleCloseIconClick = () => {
    triggerCleverTapEvent("serv_hamburger_menu_close", {
      ScreenType: SCREEN_TYPE.generalAppType,
      ScreenName: "Servicing Hamburger Menu Details Screen",
      isPrevPageNameRequired: true,
      ctaText: "close",
    });
    TriggerGTAEvent("serv_hamburger_menu_close", {
      screen_type: GTAScreenType.DashboardScreenType,
      screen_name: "servicing_hamburger_menu_details_screen",
      CTA_text: "close",
    });
    TriggerGTAEvent("serv_profile_menu_cta_click", {
      screen_type: GTAScreenType.DashboardScreenType,
      screen_name: "servicing_hamburger_menu_details_screen",
      CTA_text: "close",
    });
    setOpen(false);
    if (["hamburgerMenu", "appSettings", "logout"].includes(action)) {
      navigate(location.pathname, { replace: true });
    }
  };

  const profileHamburgerMenuData = useAppSelector(
    (state) => state.GeneralAppElementsSlice.quickActionMenu,
  )?.[111];

  const isHomeLoanExist = useMemo(
    () =>
      userDetails?.customerAndLobDetails?.some(
        (item) =>
          item.LOB === HlLobCode && item.PRIMARY_SECONDARY_USER === "primary",
      ),
    [userDetails?.customerAndLobDetails],
  );

  let mainMenuList = useMemo(() => {
    let tempList = !isEmpty(csAppSlice.NativeDeviceInfo?.deviceName)
      ? profileHamburgerMenuData?.filter((i) => i.isHamburgerMenu === true)
      : profileHamburgerMenuData?.filter(
          (i) => i.menuId !== 150 && i.isHamburgerMenu === true, // filter out app settings option for non-native devices
        );

    tempList = isHomeLoanExist
      ? tempList
      : tempList?.filter((i) => i.menuId !== 229 && i.isHamburgerMenu === true); // filter out change language option if home loan doesn't exist

    return tempList;
  }, [
    csAppSlice.NativeDeviceInfo?.deviceName,
    profileHamburgerMenuData,
    isHomeLoanExist,
  ]);

  mainMenuList = sortBy(mainMenuList, "menuOrderId");

  const cmsData = useAppSelector((state) => state.CmsSlice.CmsPageVariableData);
  const cmsLogo =
    cmsData.find((i) => i.attributes.cmsType === "ABC Logo")?.attributes.image
      .data?.attributes.url ?? "";

  const imageStyle: React.CSSProperties = {
    width: "7.125rem", // Use camelCase for width
    marginBottom: "0.75rem", // Use camelCase for margin-bottom
    height: "2.75rem",
  };

  const handleRateUsOpen = (value: boolean) => {
    setOpenRateUsPopUp(value);
    if (module === "rateUs") {
      navigate(location.pathname, { replace: true });
    }
  };

  const handleCustomPopupOpen = () => {
    setIsCustomPopupOpen(false);
    if (module === "termsAndCondition") {
      navigate(location.pathname, { replace: true });
    }
  };

  const customerName = userDetails?.IS_USER_REGISTERED
    ? userDetails?.name
      ? userDetails?.name
      : "User"
    : "User";

  const descriptionKeyMap = useMemo<Record<number, string>>(
    () => ({
      143: "hamburgerMenu.menuDescriptions.myProfile",
      144: "hamburgerMenu.menuDescriptions.utilities",
      145: "hamburgerMenu.menuDescriptions.helpSupport",
      150: "hamburgerMenu.menuDescriptions.appSettings",
      229: "hamburgerMenu.menuDescriptions.changeLanguage",
    }),
    [],
  );

  const mainMenu = (
    <>
      <Box className="header-part">
        <Box display="flex" alignItems="center">
          <Typography variant="body2" className="header-avatar">
            {getInitials(customerName)}
          </Typography>
          <Typography variant="h2">{customerName}</Typography>
        </Box>
        <CloseIcon
          fontSize="medium"
          sx={{
            marginRight: { xs: ".4375rem", sm: 0 },
          }}
          className="close-icon"
          onClick={handleCloseIconClick}
        />
      </Box>
      <Box>
        <Box paddingTop={className ? "0.25rem" : "1.5rem"}>
          {mainMenuList
            ?.filter((item) => item.menuId) // Filter items with isEnable true
            .map((item, index) => {
              const descriptionKey = descriptionKeyMap[item.menuId] ?? "";
              return (
                <React.Fragment key={item.menuId}>
                  <ListItem
                    key={item.menuId}
                    onClick={() => handleMenuClick(item)}
                    secondaryAction={
                      <IconButton
                        sx={{ marginRight: 0 }}
                        edge="end"
                        aria-label="delete"
                      >
                        <KeyboardArrowRightIcon />
                      </IconButton>
                    }
                    sx={{
                      marginY: item.menuName === "Log out" ? "8px" : "0",
                      cursor: "pointer",
                    }}
                  >
                    <ListItemAvatar>
                      <img
                        style={{
                          height: "2rem",
                          marginLeft:
                            index === mainMenuList?.length ? ".3125rem" : "0",
                        }}
                        src={item.menuIcon}
                        alt={item.menuDisplayName}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant="h5"
                          marginBottom={".25rem"}
                          // onClick={() => handleMenuClick(item)}
                        >
                          {item.jsonKey
                            ? t(item.jsonKey)
                            : item.menuDisplayName}
                        </Typography>
                      }
                      secondary={
                        item?.menuDescription && (
                          <Typography
                            fontSize={".75rem"}
                            fontWeight={400}
                            style={{ color: "#77787B" }}
                          >
                            {t(descriptionKey)}
                          </Typography>
                        )
                      }
                    />
                  </ListItem>
                  <Divider color="#F1F3F6" />
                </React.Fragment>
              );
            })}
        </Box>
      </Box>
      <Container className="bottom-part">
        <Box display="flex" justifyContent="center">
          <CustomNetworkImage
            src={cmsLogo || ABCLogo}
            alt="aditya-birla"
            style={imageStyle}
          />
        </Box>

        {csAppSlice.NativeDeviceInfo?.appVersion && (
          <Container className="app-version">
            <Typography>
              {t("hamburgerMenu.actionButtons.appVersion")}{" "}
              {csAppSlice.NativeDeviceInfo?.appVersion}
            </Typography>
          </Container>
        )}
        <Box display="flex" marginY="1.375rem" justifyContent="space-between">
          <Typography
            variant="subtitle2"
            sx={{ cursor: "pointer" }}
            onClick={() => handlePolicyTermsPopup(termsAndConditionsTitle)}
          >
            {t("hamburgerMenu.actionButtons.termsAndConditions")}
          </Typography>
          <Typography
            sx={{ cursor: "pointer" }}
            variant="subtitle2"
            onClick={() => handlePolicyTermsPopup(privacyPolicyTitle)}
          >
            {t("hamburgerMenu.actionButtons.privacyPolicy")}
          </Typography>
          <Typography
            sx={{ cursor: "pointer" }}
            variant="subtitle2"
            onClick={() => handleRateUsPopup()}
          >
            {t("hamburgerMenu.actionButtons.rateUs")}
          </Typography>
        </Box>
      </Container>
      {isCustomPopupOpen && (
        <PolicyAndConditionsComponent
          open={isCustomPopupOpen}
          handleOpen={handleCustomPopupOpen}
          title={finalPolicyAndTermsTitle}
        />
      )}

      {languagePopupOpen && (
        <LanguageSelectionPopup
          open={languagePopupOpen}
          handleOpen={setLanguagePopupOpen}
        />
      )}

      {logoutPopupOpen && (
        <LogOutPopUp open={logoutPopupOpen} handleOpen={setLogoutPopupOpen} />
      )}
      {openRateUsPopUp && (
        <RateUsPopUp open={openRateUsPopUp} handleOpen={handleRateUsOpen} />
      )}
    </>
  );

  const handleBackBtnClick = (pathname: string) => {
    if (pathname === "help-&-support" && fromHomeSection) {
      setOpen(false);
      return;
    }

    if (
      ["myProfile", "utilities", "helpAndSupport", "appSettings"].includes(
        action,
      )
    ) {
      navigate(location.pathname, { replace: true });
    }
    if (
      ["my-profile", "utilities", "help-&-support", "app-settings"].includes(
        pathname,
      )
    ) {
      setVisibleComponent("main-menu");
      TriggerGTAEvent("back_click", {
        screen_type: "servicing_homescreen_dashboard",
      });
    } else if (pathname === "FAQs") {
      setVisibleComponent("help-&-support");
    } else if (
      [
        "change-mPIN",
        "device-authentication-settings",
        "permission-settings",
      ].includes(pathname)
    ) {
      setVisibleComponent("app-settings");
    }
  };

  const renderComponent = () => {
    switch (visibleComponent) {
      case "main-menu":
        return mainMenu;
      case "my-profile":
        return (
          <MyProfile
            handleBackBtnClick={handleBackBtnClick}
            pathname={visibleComponent}
          />
        );
      case "utilities":
        return (
          <Utilities
            handleBackBtnClick={handleBackBtnClick}
            pathname={visibleComponent}
          />
        );
      case "help-&-support":
        return (
          <HelpAndSupport
            handleBackBtnClick={handleBackBtnClick}
            pathname={visibleComponent}
            setVisibleComponent={setVisibleComponent}
          />
        );
      case "app-settings":
        return (
          <AppSettings
            handleBackBtnClick={handleBackBtnClick}
            pathname={visibleComponent}
            setVisibleComponent={setVisibleComponent}
          />
        );
      case "FAQs":
        return (
          <FAQs
            handleBackBtnClick={handleBackBtnClick}
            pathname={visibleComponent}
          />
        );
      case "change-mPIN":
        return (
          <ChangemPIN
            handleBackBtnClick={handleBackBtnClick}
            pathname={visibleComponent}
          />
        );
      case "device-authentication-settings":
        return (
          <DeviceAuthenticationSettings
            handleBackBtnClick={handleBackBtnClick}
            pathname={visibleComponent}
          />
        );
      case "permission-settings":
        return (
          <AppPermissions
            handleBackBtnClick={handleBackBtnClick}
            pathname={visibleComponent}
          />
        );
      default:
        return mainMenu;
    }
  };

  const handlePolicyTermsPopup = (title: string) => {
    handleCleverTapBottomCTAClick(title);
    handleGTMBottomCTAClick(title);
    setIsCustomPopupOpen(true);
    TriggerGTAEvent("serv_profile_menu_cta_click", {
      screen_type: GTAScreenType.DashboardScreenType,
      screen_name: "servicing_hamburger_menu_details_screen",
      CTA_text: title,
    });

    title === termsAndConditionsTitle
      ? setFinalPolicyAndTermsTitle(termsAndConditionsTitle)
      : setFinalPolicyAndTermsTitle(privacyPolicyTitle);
  };

  const handleRateUsPopup = () => {
    TriggerGTAEvent("serv_profile_menu_cta_click", {
      screen_type: GTAScreenType.DashboardScreenType,
      screen_name: "servicing_hamburger_menu_details_screen",
      CTA_text: "Rate Us",
    });
    handleCleverTapBottomCTAClick("Rate Us");
    handleGTMBottomCTAClick("Rate Us");
    setOpenRateUsPopUp(true);
  };

  return (
    <Dialog
      className={`hamburger-menu-dialog ${
        isSuperApp ? "no-tap-highlights" : ""
      } ${className}`}
      TransitionComponent={Transition}
      fullScreen
      open={open}
    >
      {renderComponent()}
    </Dialog>
  );
};

export default HamburgerMenuDialog;
